import Backbone from 'backbone'
import {Wob} from './wob'
import _ from 'lodash'
import Magellan from 'magellan'
import pyramid from './support/pyramid'
import { EventEmitter } from 'events'
import {Connectors} from './wob'

const Wobs = Backbone.Collection.extend({

  model: Wob,

  initialize: function (models, options) {
    var options = this.options = options || {}
    this.widgetTypes = options.widgetTypes

    _.bindAll(this, 'onRemoved', 'onModified', 'onCreated', 'onRestored')

    this.listenTo(this, 'sync', this.syncd, this)

    this.ee = options.ee || new EventEmitter
    this.ee.on('wob:created', this.onCreated)
    this.ee.on('wob:modified', this.onModified)
    this.ee.on('wob:removed', this.onRemoved)
    this.ee.on('wob:restored', this.onRestored)

    this.listenTo(this, 'change:levitated resize', this.adjustZIndex)
  },

  set: function () {
    Backbone.Collection.prototype.set.apply(this, arguments)
    var self = this
    this.each(function (wob) {
      if (!(wob.get('connectors') instanceof Backbone.Collection)) {
        // There's a chance we have wob objects with an array of connectors (not a connectors collection)
        // from popping the history. Turn those into a real collection
        wob.set('connectors', new Connectors(wob.get('connectors'), {wob: wob}))
      }
      if (wob.get('connectors')) {
        wob.get('connectors').each(function (connector) {
          if (connector.get('to')) {
            // Make sure the `to` field is referencing a real wob
            connector.set('to', self.get(connector.get('to')), {silent: true})
          }
        })
        wob.trigger('change:connectors') // Force a redraw so the connector is drawn
      }
    })
    return this
  },

  /*
   * Fired anytime a wob is syncd with the server. We store the latest
   * values from the server on this wob
   */
  syncd: function (wob) {
    wob.serverState = wob.toJSON()
  },

  syncAll: function () {
    var self = this
    this.each(function (wob) {
      self.syncd(wob)
    })
  },

  clearSelectedConnectors: function () {
    this.forEach(function (wob) {
      wob.get('connectors')
         .filter(function (connector) {
           return connector.get('selected')
         })
         .forEach(function (connector) {
           connector.set('selected', false)
         })
    })
  },

  /*
   * If we're saving a wob and there is an error,
   * restore its previous server state
   */
  error: function (model, xhr) {
    model.set(model.serverState)
    model.trigger('resize move', this)
  },

  /*
   * Stacks a group of connect wobs:
   *  - First groups wobs based on the `levitated` property, which indicates they should be stacked on top of other wobs.
   *  - Sorts group based on the wob size (pyramid)
   *  - Then sorts wobs of the same size based on the collection's `wobComparator`
   *  - Breaks ties by sorting by the natural wob position within the collection.
   */
  stack: function (wobs) {
    var grouped = wobs.reduce(function (p, c) {
                        // reduce into two groups, one for wobs that are levitated
                        // and should be on top of other wobs
                        if (c.levitated) {
                          p.front.push(c)
                        } else {
                          p.back.push(c)
                        }
                        return p
                      }, {front: [], back: []})
      , secondarySort = (a, b) => {
        // After pyramid sorts based on the wob size, we want to break ties by first using the `wobComparator` defined
        // by canvas, and then break those ties based on on the wob index within the collection
        return this.wobComparator(a, b) || a.idx - b.idx
      }
    // Apply pyramid (size based sorting) to each stack, followed by
    return pyramid(grouped.back, secondarySort)// stack the back
            .concat(pyramid(grouped.front, secondarySort)) // stack the front, then merge w/ the back
  },

  /*
   * Adjusts the z-index for all wobs connected to this wob.
   * Finds all wobs that are touching this one, and arranges each
   * z-index from largest at the bottom, to smallest (area based) on
   * top.
   *
   * Returns all wobs which have an adjusted z-index
   */
  adjustZIndex: function (wob) {
    if (!this.ready) {
      // The canvas hasn't marked the collection of wobs as being ready, so prevent
      // doing any work at this point since the canvas is still being built.
      // See https://github.com/SpiderStrategies/Scoreboard/issues/22375
      return
    }

    var self = this

    return this.stack(this.connected(wob))
               .filter(function (wob, i) {
                 // the ID returned by connected() is the wob's cid, since new unsaved wobs
                 // won't have a real ID yet
                 var _wob = self.get({cid: wob.id})
                   , original = _wob.get('z-index')

                 // Set the new z-index
                 _wob.set('z-index', i + 1)

                 // Filter out wobs that didn't change
                 return original !== (i + 1)
               })
  },

  /*
   * Returns an object that describes the dimensions of a box that contains all wobs.
   * This ignores wob connectors
   */
  boundingBox: function () {
    let box = this.reduce(function (p, c) {
      var bb = c.boundingBox()
      return {
        minX: Math.min(p.minX, bb.left),
        maxX: Math.max(p.maxX, bb.right),
        minY: Math.min(p.minY, bb.top),
        maxY: Math.max(p.maxY, bb.bottom)
      }
    }, {
      minX: Infinity,
      maxX: -Infinity,
      minY: Infinity,
      maxY: -Infinity
    })

    // Some code prefers using left/top style coords, so let's support all parties.
    box.left = box.minX
    box.right = box.maxX
    box.top = box.minY
    box.bottom = box.maxY

    return box
  },

  /*
   * Returns the connected graph of all wobs that are connected
   * to this wob, sorted by their z-index. We sort so stack order
   * is preserved if wob sizes didn't change.
   */
  connected: function (wob) {
    var plucked = this.map(wob => {
      var result = wob.boundingBox()
      // use the wob's cid, since new unsaved wobs won't have an ID yet
      result.id = wob.cid
      result.widget = wob.widget
      result.idx = wob.id || wob.cid // Needed for tie breaking sorts
      result.levitated = wob.get('levitated')
      result['z-index'] = wob.get('z-index')
      return result
    })
    var m = new Magellan
    plucked.forEach(function (p) {
      m.add(p)
    })

    return m.explore(plucked[this.indexOf(wob)])
            .sort((a, b) => a['z-index'] - b['z-index'])
  },

  onRestored: function (wobs) {
    this.reset(wobs)
    this.syncAll()
  },

  onRemoved: function (wob) {
    this.remove(wob._id)
  },

  onModified: function (_wob) {
    var wob = this.get(_wob._id)
    wob.set(_wob)
    var changed = wob.changed
    if ('width' in changed || 'height' in changed) {
      wob.trigger('resize', wob)
    } else {
      wob.trigger('move', wob)
    }
    this.syncd(wob)
  },

  onCreated: function (wob) {
    var wob = this.add(wob)
    this.syncd(wob)
  },

  /*
   * Performs a transformation on the set of wobs.
   * e.g. If we have a set of wobs [{50, 50}, {100, 100}, {500, 500}] and the transform
   * wants to move to 0,0, the wobs set will be transformed to [{0, 0}, {50, 50}, {450, 450}]
   */
  transform: function (x, y) {
    if (this.length === 0) {
      // Early exit
      return
    }
    let bb = this.boundingBox()
      , transformX = x - bb.minX
      , transformY = y - bb.minY

    this.forEach(wob => {
      wob.set({
        left: wob.get('left') + transformX,
        top: wob.get('top') + transformY
      }, {animate: true})
      wob.trigger('move', wob, {animate: true})
    })

    return this
  },

  /*
   * This is a shitty name.
   * The collection of wobs holds a bunch of wob models. Each
   * wob model has a property called widget, which is NOT stored
   * as a backbone attribute. This method will return all wobs
   * with their widgets as a JSON object.
   */
  toJSONWithWidgets: function () {
    return this.map(function (wob) {
      var _wob = _.omit(wob.attributes, 'connectors') // Strip out connectors to avoid cyclical references, we'll store connectors next
      _wob.widget = wob.widget.toJSON()

      // Store a simplified version of connectors. We can't store `to` objects because if wob A connects to wob B, and wob B has a connector
      // to wob A, we'd be stuck in an infinite reference cycle
      _wob.connectors = wob.get('connectors').map(function (connector) {
        return {
          id: connector.id,
          style: connector.get('style'),
          control: connector.get('control'),
          to: (connector.get('to') && connector.get('to').id) || null,
          fromId: connector.get('fromId')
        }
      })

      // Keep the cid so history can recreate it exactly
      _wob.cid = wob.cid
      return _wob
    })
  },

  release: function () {
    this.ee.removeListener('wob:modified', this.onModified)
    this.ee.removeListener('wob:created', this.onCreated)
    this.ee.removeListener('wob:removed', this.onRemoved)
    this.ee.removeListener('wob:restored', this.onRestored)
    // Release each wob model
    this.forEach(function (wob) {
      wob.release()
    })
  }

})

Wobs.filters = {
  unlocked: function unlocked (wob) {
    return !wob.get('locked') && !wob.get('readonly')
  }
}

export default Wobs

